// Cmd-K command palette. Cross-entity search over bookings, customers,
// vendors, cars, and locations.
//
// Opens on Cmd/Ctrl-K from anywhere in the authed shell (handler lives in
// AdminLayout). Keyboard nav: ↑/↓ move selection, Enter navigates,
// Escape closes. Debounced 200ms to avoid hammering the API on every
// keystroke.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { fetchAdminSearch, type AdminSearchHit } from '@/lib/admin-api';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface FlatHit {
  group: GroupKey;
  hit: AdminSearchHit;
}

type GroupKey = 'bookings' | 'customers' | 'vendors' | 'cars' | 'locations';

const GROUP_LABEL: Record<GroupKey, string> = {
  bookings: 'Bookings',
  customers: 'Customers',
  vendors: 'Vendors',
  cars: 'Cars',
  locations: 'Locations',
};

const GROUP_TONE: Record<GroupKey, string> = {
  bookings: 'bg-[#eff6ff] text-[#1e3a8a]',
  customers: 'bg-[#ecfdf5] text-[#065f46]',
  vendors: 'bg-[#fef3c7] text-[#92400e]',
  cars: 'bg-[#fef2f2] text-[#9f1239]',
  locations: 'bg-[#eef2ff] text-[#1e1b4b]',
};

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Debounce search input — 200ms is the sweet spot between feeling
  // responsive and not running a query on every keystroke during a paste.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(rawQuery.trim()), 200);
    return () => clearTimeout(id);
  }, [rawQuery]);

  // Reset state on open so the user always sees a fresh palette.
  useEffect(() => {
    if (open) {
      setRawQuery('');
      setDebouncedQuery('');
      setSelectedIndex(0);
      // Defer the focus by a frame so the dialog has mounted.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'search', debouncedQuery],
    queryFn: ({ signal }) => fetchAdminSearch(debouncedQuery, signal),
    enabled: open && debouncedQuery.length >= 1,
  });

  const flat = useMemo<FlatHit[]>(() => {
    if (!data) return [];
    const out: FlatHit[] = [];
    (Object.keys(data.hits) as GroupKey[]).forEach((g) => {
      const hits = data.hits[g] as AdminSearchHit[];
      for (const hit of hits) out.push({ group: g, hit });
    });
    return out;
  }, [data]);

  // Clamp the selection when the result set changes underneath us.
  useEffect(() => {
    if (selectedIndex >= flat.length) {
      setSelectedIndex(Math.max(0, flat.length - 1));
    }
  }, [flat.length, selectedIndex]);

  const onSelect = (entry: FlatHit) => {
    onClose();
    void navigate({ to: entry.hit.to as never });
  };

  // Keyboard nav. Bound to document so we catch arrows even when the
  // input is focused.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(flat.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const entry = flat[selectedIndex];
        if (entry) onSelect(entry);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flat, selectedIndex]);

  if (!open) return null;

  // Group results in the visual order they appear in `flat` so the
  // arrow-key index lines up with the rendered rows.
  let cursor = 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Quick search"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 px-4 pt-[12vh]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-xl border border-[color:var(--bo-line)] bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[color:var(--bo-line)] px-4 py-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="h-4 w-4 shrink-0 text-[color:var(--bo-mute)]"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Search bookings, customers, vendors, cars, locations…"
            className="flex-1 bg-transparent text-[14px] text-[color:var(--bo-ink)] outline-none placeholder:text-[color:var(--bo-mute)]"
          />
          <span className="rounded-md border border-[color:var(--bo-line)] bg-white px-1.5 py-0.5 text-[10px] font-medium text-[color:var(--bo-mute)]">
            esc
          </span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {error ? (
            <div className="px-4 py-3 text-[12.5px] text-[#9f1239]">
              Search failed: {(error as Error).message}
            </div>
          ) : null}

          {!debouncedQuery ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-[color:var(--bo-mute)]">
              Type to search. Use{' '}
              <kbd className="rounded border border-[color:var(--bo-line)] bg-white px-1 font-mono text-[11px]">
                ↑↓
              </kbd>{' '}
              to navigate,{' '}
              <kbd className="rounded border border-[color:var(--bo-line)] bg-white px-1 font-mono text-[11px]">
                ↵
              </kbd>{' '}
              to open.
            </div>
          ) : isLoading ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-[color:var(--bo-mute)]">
              Searching…
            </div>
          ) : flat.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-[color:var(--bo-mute)]">
              No matches for{' '}
              <span className="font-mono">{debouncedQuery}</span>.
            </div>
          ) : (
            (Object.keys(GROUP_LABEL) as GroupKey[]).map((g) => {
              const groupHits = data?.hits[g] ?? [];
              if (groupHits.length === 0) return null;
              return (
                <div key={g} className="border-b border-[color:var(--bo-line)] last:border-b-0">
                  <div className="px-4 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
                    {GROUP_LABEL[g]}
                  </div>
                  <ul>
                    {groupHits.map((hit) => {
                      const idx = cursor++;
                      const isSelected = idx === selectedIndex;
                      return (
                        <li key={`${g}-${hit.id}`}>
                          <button
                            type="button"
                            onClick={() => onSelect({ group: g, hit })}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            className={`flex w-full items-center gap-3 px-4 py-2.5 text-start transition ${
                              isSelected
                                ? 'bg-[color:var(--bo-bg)]'
                                : 'hover:bg-[color:var(--bo-bg)]/60'
                            }`}
                          >
                            <span
                              className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${GROUP_TONE[g]}`}
                            >
                              {g.slice(0, -1)}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[13px] font-medium text-[color:var(--bo-ink)]">
                                {hit.label}
                              </span>
                              {hit.sublabel ? (
                                <span className="block truncate text-[11.5px] text-[color:var(--bo-mute)]">
                                  {hit.sublabel}
                                </span>
                              ) : null}
                            </span>
                            {hit.status ? (
                              <span className="shrink-0 rounded-md border border-[color:var(--bo-line)] bg-white px-1.5 py-0.5 text-[10px] capitalize text-[color:var(--bo-mute)]">
                                {hit.status}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
